import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "../api/client";

const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  const [customers, setCustomers] = useState([]);
  const [salesPeople, setSalesPeople] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({}); // id -> full detail payload
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  }, []);

  const refreshCustomers = useCallback(() => api.customers().then(setCustomers), []);
  const refreshSalesPeople = useCallback(() => api.salesPeople().then(setSalesPeople), []);
  // These two hit admin-only endpoints. team_lead/office legitimately get 403 here -
  // that's expected, not an error, so callers must never let a rejection here bubble
  // up as if the real action (creating a plan, etc.) had failed.
  const refreshPending = useCallback(() => api.pendingPayments().then(setPendingPayments).catch(() => {}), []);
  const refreshDashboard = useCallback(() => api.dashboard().then(setDashboardStats).catch(() => {}), []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([refreshCustomers(), refreshSalesPeople(), refreshPending(), refreshDashboard()]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [refreshCustomers, refreshSalesPeople, refreshPending, refreshDashboard]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const fetchCustomerDetail = useCallback(async (id) => {
    const data = await api.customer(id);
    setCustomerDetails((prev) => ({ ...prev, [id]: data }));
    return data;
  }, []);

  async function confirmPayment(paymentId) {
    const payment = pendingPayments.find((p) => p.id === paymentId);
    await api.confirmPayment(paymentId);
    showToast(
      payment
        ? `Confirmed ${naira(payment.amount)} for ${payment.customer_name}. Balance updated.`
        : "Payment confirmed."
    );
    await Promise.allSettled([refreshPending(), refreshCustomers(), refreshDashboard()]);
    if (payment?.customer_id) fetchCustomerDetail(payment.customer_id);
  }

  async function rejectPayment(paymentId, note) {
    const payment = pendingPayments.find((p) => p.id === paymentId);
    await api.rejectPayment(paymentId, note);
    showToast(payment ? `Rejected claim from ${payment.customer_name}.` : "Payment rejected.");
    await Promise.allSettled([refreshPending(), refreshDashboard()]);
    if (payment?.customer_id) fetchCustomerDetail(payment.customer_id);
  }

  async function addPlanToCustomer(customerId, planForm) {
    const payload = {
      customer: customerId,
      equipment_name: planForm.equipment_name,
      total_price: planForm.total_price,
      date_purchased: planForm.date_purchased,
      duration_months: planForm.duration_months || null,
      sales_person: planForm.sales_person,
      amount_paid: planForm.amount_paid || 0,
    };
    const data = await api.createPlan(payload); // { plan, customer } - the real action, must succeed or throw for real
    await Promise.allSettled([refreshCustomers(), refreshPending(), refreshDashboard(), fetchCustomerDetail(customerId)]);
    return data;
  }

  async function revertPayment(paymentId) {
    await api.revertPayment(paymentId);
    await Promise.allSettled([refreshPending(), refreshCustomers(), refreshDashboard()]);
  }
  

  async function addCustomerWithPlan(customerForm, planForm, linkUserId = null) {
    const payload = {
      name: customerForm.name,
      phone: customerForm.phone,
      email: customerForm.email,
      equipment_name: planForm.equipment_name,
      total_price: planForm.total_price,
      date_purchased: planForm.date_purchased,
      duration_months: planForm.duration_months || null,
      sales_person: planForm.sales_person,
      amount_paid: planForm.amount_paid || 0,
    };
    if (linkUserId) payload.link_user_id = linkUserId;

    const result = await api.createCustomer(payload);
    await Promise.allSettled([refreshCustomers(), refreshPending(), refreshDashboard()]);
    return result;
  }

  async function addSalesPerson(name) {
    const person = await api.createSalesPerson(name);
    await refreshSalesPeople();
    return person;
  }

  async function checkCustomerMatches(email, phone) {
    if (!email && !phone) return [];
    const res = await api.checkExistingCustomer(email, phone);
    return res.matches;
  }

  const value = {
    customers,
    salesPeople,
    pendingPayments,
    dashboardStats,
    customerDetails,
    refreshCustomers,
    revertPayment,
    loading,
    error,
    fetchCustomerDetail,
    confirmPayment,
    rejectPayment,
    addPlanToCustomer,
    addCustomerWithPlan,
    addSalesPerson,
    refreshAll,
    toast,
    showToast,
    checkCustomerMatches
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be used within AppDataProvider");
  return ctx;
}

// local helper (kept here so confirm/reject toasts don't need a separate import)
function naira(n) {
  return "\u20a6" + Number(n || 0).toLocaleString("en-NG");
}